import { AlertDefaults, AlertType, ChannelSettings, PriorityType } from 'src/app/hickory-api';
import { RetryInfo } from 'src/app/shared/model';
import { AlertModelService } from '../alert-model.service';
import { AlertFields } from './alert-fields-helper';
import { AlertWorkflowHelper } from './alert-workflow-helper';
import { AlertPriorityHelper } from './alert-priority-helper';
import { SourceE911, SourceInfoCode, SourceMyAlerts } from './alert-geofences-helper';
import { IncidentModelHelper } from 'src/app/shared/incident';
import { toFriendlyString, toPascalCase, toString } from 'src/app/shared/model/utility';
import { L10nTranslationService } from 'angular-l10n';
import { SocialChannel } from './alert-social-helper';
import { PlaceholderUtilities } from 'src/app/shared/templated-input/placeholder-utilities';
import { HtmlHelper } from 'src/app/shared/html-helper/html-helper';
import { AlertMicrosoftTeamsHelper } from './alert-microsoft-teams-helper';
import { SlicePipe } from '@angular/common';

export interface BasicChannel {
  channelKey?: string,
  channelName?: string,
  send?: boolean,
  message?: string,
  channelMessage?: string, // for voice channel, b/c 'message' is used for the audio file name 🙄
}

export class AlertChannelsHelper {
  static readonly ERROR_AUDIO_FILE_NOT_FOUND = 'Alerts_ValidationError_AudioFileNotFound';

  static readonly KEY_MOBILE_APP = 'push';
  static readonly KEY_MYALERTS_APP = 'myAlertsApp';
  static readonly KEY_EMAIL = 'email';
  static readonly KEY_TEXT_MESSAGE = 'textMessage';
  static readonly KEY_VOICE = 'voice';
  static readonly KEY_FAX = 'fax';
  static readonly KEY_EAS = 'eas';
  static readonly KEY_ETN = 'etn';
  static readonly KEY_FACEBOOK = 'facebook';
  static readonly KEY_TWITTER = 'twitter';
  static readonly KEY_NEXTDOOR = 'nextdoor';
  static readonly KEY_MICROSOFT_TEAMS = 'microsoftTeams';

  static readonly NAME_MOBILE_APP = 'Mobile App';
  static readonly NAME_MYALERTS_APP = 'myAlerts App';
  static readonly NAME_EMAIL = 'Email';
  static readonly NAME_TEXT_MESSAGE = 'Text Message';
  static readonly NAME_VOICE = 'Voice';
  static readonly NAME_FAX = 'Fax';
  static readonly NAME_EAS = 'EAS';
  static readonly NAME_ETN = 'ETN';
  static readonly NAME_FACEBOOK = 'Facebook';
  static readonly NAME_TWITTER = 'X (Twitter)';
  static readonly NAME_NEXTDOOR = 'Nextdoor';
  static readonly NAME_MICROSOFT_TEAMS = 'Microsoft Teams';

  /**
   * Map of channel `key` to channel `name` (i.e. { 'push': 'Mobile App', ... } )
   */
  static readonly CHANNELS_MAP = {
    [this.KEY_MOBILE_APP]: this.NAME_MOBILE_APP,
    [this.KEY_MYALERTS_APP]: this.NAME_MYALERTS_APP,
    [this.KEY_EMAIL]: this.NAME_EMAIL,
    [this.KEY_TEXT_MESSAGE]: this.NAME_TEXT_MESSAGE,
    [this.KEY_VOICE]: this.NAME_VOICE,
    [this.KEY_FAX]: this.NAME_FAX,
    [this.KEY_EAS]: this.NAME_EAS,
    [this.KEY_ETN]: this.NAME_ETN,
    [this.KEY_FACEBOOK]: this.NAME_FACEBOOK,
    [this.KEY_TWITTER]: this.NAME_TWITTER,
    [this.KEY_NEXTDOOR]: this.NAME_NEXTDOOR,
    [this.KEY_MICROSOFT_TEAMS]: this.NAME_MICROSOFT_TEAMS,
  }

  /**
   * Map of channel `name` to localization key (i.e. { 'Mobile App': 'Alert.Channels.MobileApp', ... } )
   * Most are just the channel name (i.e. { 'Email': 'Email', ... } )
   */
  static readonly LOCALIZED_CHANNELS_MAP = {
    [this.NAME_MOBILE_APP]: 'Alert.Channels.MobileApp',
    [this.NAME_MYALERTS_APP]: 'Alert.Channels.MyAlertsApp',
    [this.NAME_TEXT_MESSAGE]: 'Alert.Channels.TextMessage',
    [this.NAME_MICROSOFT_TEAMS]: 'Alert.Channels.MicrosoftTeams',
  }

  private static readonly slicePipe = new SlicePipe()

  static allChannelKeys(): Array<string> {
    return Object.keys(this.CHANNELS_MAP);
  }

  static getChannelName(channelKey: string): string {
    return this.CHANNELS_MAP[channelKey] ?? toFriendlyString(channelKey);
  }

  static getChannelLocalizationKey(channelName: string): string {
    return this.LOCALIZED_CHANNELS_MAP[channelName] ?? channelName;
  }

  static iterableChannels(service: AlertModelService, channels: ChannelSettings = service.alertChannels, setAdditional: boolean = true): BasicChannel[] {
    return Object.keys(channels || {}).map(k => {
      const channel = channels[k] as BasicChannel;
      if (!!channel && setAdditional) {
        channel.channelKey = k;
        channel.channelName = this.getChannelName(k);
      }
      return channel;
    }).filter(c => !!c);
  }

  static postProcess(service: AlertModelService, alert: AlertDefaults = service.alertSettings) {
    const channels = alert.channels;
    if (!channels) return;
    this.clearEmptyChannelMessages(channels);
    // mobileBroadcast is no longer supported
    delete channels.mobileBroadcast;
    // ROAD-9322: disallow ETN or myAlerts for polls
    if (alert.alertType === AlertType.Poll) {
      delete channels.etn;
      delete channels.myAlertsApp;
    }
    
    // if the message on the channel is the same as the main alert message, clear out the channel message
    // -- a null/empty channel message means it's not customized; a non-empty channel message means it's customized
    const alertMessage = alert.message.basic.message;
    if (alertMessage) { // main alert message is non-empty
      // voice, facebook, microsoftTeams -- no character limits, so check for equality
      if (alertMessage === alert?.channels?.voice?.message) alert.channels.voice.message = undefined;
      if (alertMessage === alert?.channels?.facebook?.message) alert.channels.facebook.message = undefined;
      if (alertMessage === alert?.channels?.microsoftTeams?.message) alert.channels.microsoftTeams.message = undefined;
      // textMessage, twitter, nextdoor -- character limits enforced, so check for startsWith
      if (alertMessage.startsWith(alert?.channels?.textMessage?.message)) alert.channels.textMessage.message = undefined;
      if (alertMessage.startsWith(alert?.channels?.twitter?.message)) alert.channels.twitter.message = undefined;
      if (alertMessage.startsWith(alert?.channels?.nextdoor?.message)) alert.channels.nextdoor.message = undefined;
    }
  }

  static preparePreview(service: AlertModelService, preview: AlertDefaults) {
    const channels = preview.channels;
    if (!channels) return;

    this.clearEmptyChannelMessages(channels);

    // ROAD-8731: make sure ETN alerts are always "Emergency" priority
    if (channels.etn?.send === true && !!service?.alertMessage && service.alertMessage.priority !== PriorityType.Emergency) {
      preview.message.priority = PriorityType.Emergency;
      service.alertMessage.priority = PriorityType.Emergency;
      service.notifyFieldUpdated(AlertFields.PRIORITY);
    }
    
    if(!service.useVoicePriority) preview.prioritySettings = null;
  }

  static clearEmptyChannelMessages(channels: ChannelSettings) {
    // ROAD-6396 - don't send or save an empty channel.message
    this.iterableChannels(null, channels, false).forEach(iterable => {
      const channel = iterable;
      if (channel.message?.trim()?.length === 0) channel.message = undefined; // textMessage, etc
      if (channel.channelMessage?.trim()?.length === 0) channel.channelMessage = undefined; // voice TTS is saved as "channelMessage"
    });
  }

  static autoUpdateChannelSelections(service: AlertModelService, alert: AlertDefaults = service?.alertSettings, channels: ChannelSettings = alert?.channels) {
    if (!alert || !channels) return;
    const snapshot = JSON.stringify(channels);

    // ROAD-8953 - don't auto-select ETN and myAlerts for now
    // this.autoUpdateMyAlertsSelection(alert, channels);
    // this.autoUpdateEtnSelection(alert, channels);

    this.autoEnableVoiceSelection(alert, channels);

    alert.channels = channels;
    if (snapshot !== JSON.stringify(channels)) {
      service?.notifyFieldUpdated(AlertFields.CHANNELS);
    }
  }

  static autoEnableVoiceSelection(alert: AlertDefaults, channels: ChannelSettings = alert.channels): boolean {
    let didChange = false;
    if (channels.voice && AlertWorkflowHelper.isVoiceRequired(alert)) {
      // only update if required by workflow or priority
      if (!channels.voice.send) {
        channels.voice.send = true;
        didChange = true;
      }
      if (!channels.voice.businessPhone) {
        channels.voice.businessPhone = true;
        didChange = true;
      }
      if (!channels.voice.cellPhone) {
        channels.voice.cellPhone = true;
        didChange = true;
      }
      if (!channels.voice.homePhone) {
        channels.voice.homePhone = true;
        didChange = true;
      }
    }
    return didChange;
  }

  static autoUpdateMyAlertsSelection(alert: AlertDefaults, channels: ChannelSettings = alert.channels) {
    if (channels.myAlertsApp) {
      let send = false;
      send = send || !!alert.recipients?.featureLayers?.find(l => l.FeaturesSource === SourceMyAlerts);
      send = send || !!alert.recipients?.municipals?.sendTo?.find(l => l.FeaturesSource === SourceMyAlerts);
      channels.myAlertsApp.send = send;
    }
  }

  static autoUpdateEtnSelection(alert: AlertDefaults, channels: ChannelSettings = alert.channels): boolean {
    let didChange = false;
    if (channels.etn) {
      const sendBefore = channels.etn.send;
      let send = false;
      send = send || !!alert.recipients?.featureLayers?.find(l => l.FeaturesSource === SourceE911);
      send = send || !!alert.recipients?.featureLayers?.find(l => l.FeaturesSource === SourceInfoCode);
      channels.etn.send = send;
      didChange = sendBefore !== send;
    }
    return didChange;
  }

  // if ETN is selected to send and priority != Emergency, we need to deselect ETN and deselect ETN layers
  // Use case: the user selected a new priority or a new incident
  static autoDeselectEtnIfNonEmergency(service: AlertModelService, alert: AlertDefaults = service.alertSettings, channels: ChannelSettings = alert.channels): boolean {
    let didChange = false;
    if (channels.etn?.send === true && AlertPriorityHelper.getOptionalAlertPriority(alert.message) !== PriorityType.Emergency) {
      didChange = true;
      channels.etn.send = false;
      if (alert.recipients?.featureLayers) {
        alert.recipients.featureLayers = alert.recipients.featureLayers.filter(l => l.FeaturesSource !== SourceE911 && l.FeaturesSource !== SourceInfoCode);
      }
      if (service?.mapSelection?.SourceRecords) {
        service.mapSelection?.SourceRecords?.forEach(r => {
          if (r.LayerDef?.featuresSource === SourceE911 || r.LayerDef?.featuresSource === SourceInfoCode) {
            r.Selected = false;
          }
        });
      }
      service?.notifyFieldUpdated(AlertFields.CHANNELS);
      service?.notifyFieldUpdated(AlertFields.GEO_FENCING);
    }

    return didChange;
  }

  // if ETN is selected to send and priority != Emergency, we need to auto-select Emergency as the priority
  // Use case: the user selected the ETN delivery method (channel) or ETN layers were selected
  static autoCheckPriorityAgainstEtn(service: AlertModelService, alert: AlertDefaults = service?.alertSettings): boolean {
    if (AlertPriorityHelper.getOptionalAlertPriority(alert.message) === PriorityType.Emergency) return false;
    let willSendEtn = true;
    willSendEtn = willSendEtn && alert?.channels?.etn?.send === true;
    willSendEtn = willSendEtn && alert?.recipients?.featureLayers?.some(l => l.FeaturesSource === SourceE911 || l.FeaturesSource === SourceInfoCode) === true;
    if (!willSendEtn) return false;
    
    // if the priority doesn't match but we've got an incident on the alert, we can't change it automatically
    if (IncidentModelHelper.hasIncident(alert.message)) return false;

    AlertPriorityHelper.onPriorityChange(service, alert, PriorityType.Emergency);
    // prevent duplicate triggers to this while we are fetching defaults
    alert.message.priority = PriorityType.Emergency;
    return true;
  }

  static summaryListLocalizationKeys(channels: ChannelSettings): string[] {
    const list = this.channelNamesEnabledForSend(channels).map(n => this.getChannelLocalizationKey(n));
    return (list.length) ? list : ['None Selected'];
  }

  static channelKeysEnabledForSend(channels: ChannelSettings): string[] {
    const list: string[] = [];
    Object.keys(channels || {}).forEach(k => {
      const channel = channels[k] as BasicChannel;
      if (channel?.send) list.push(k);
    });
    return list;
  }

  static channelNamesEnabledForSend(channels: ChannelSettings): string[] {
    return this.channelKeysEnabledForSend(channels).map(k => this.getChannelName(k));
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  static validate(service: AlertModelService, channels: ChannelSettings = service.alertChannels): string[] {
    const errors: string[] = [];

    // check that a channel is selected
    if (!this.iterableChannels(service, channels).find(x => x.send)) {
      errors.push('Alert.Channels.SelectDeliveryMethod');
    }

    // check text channel
    const textChannel = channels?.textMessage;
    if (textChannel?.send && !(textChannel?.primary || textChannel?.secondary)) {
      errors.push('Alert.Channels.InvalidTextMessageChannelOptions');
    }

    // check voice channel
    const voiceChannel = channels?.voice;
    if (voiceChannel?.send && !(voiceChannel?.cellPhone || voiceChannel?.businessPhone || voiceChannel?.homePhone)) {
      errors.push('Alert.Channels.InvalidVoiceChannelOptions');
    }

    // check placeholders
    if (service.alertMessage.customMessageVariables?.useMessageVariables ?? false) {
      const placeholderErrors = this.validatePlaceholdersForChannels(service);
      errors.push(...placeholderErrors);

      // check for contact-specific placeholders in public content
      const publicContentError = this.validatePlaceholdersForPublicContent(service);
      if (publicContentError) {
        errors.push(publicContentError);
      }
    }

    // check etn channel
    const hasMapSelection = !!service?.alertRecipients?.geofences?.features?.length;
    const geoFeatureLayers = service?.alertRecipients?.featureLayers;
    const e911Layer = geoFeatureLayers?.find(x => x.FeaturesSource === SourceE911)
    const infoCodeLayer = geoFeatureLayers?.find(x => x.FeaturesSource === SourceInfoCode)
    const hasEtnLayer = hasMapSelection && (!!e911Layer || !!infoCodeLayer);
    const isEtnSelected = !!channels?.etn?.send;
    if (hasEtnLayer !== isEtnSelected) {
      if (isEtnSelected) errors.push('Alert.Channels.InvalidEtnChannelRequiresSelection');
      else errors.push('Alert.Channels.InvalidEtnChannelSelectionRequired');
    }
    if (hasEtnLayer && isEtnSelected && service.alertPriority !== PriorityType.Emergency) {
      errors.push('Alert.Channels.InvalidEtnChannelEmergencyPriority');
    }

    // check myAlerts channel
    const municipalFeatureLayers = service?.alertRecipients?.municipals?.sendTo;
    const myAlertsMapLayer = geoFeatureLayers?.find(x => x.FeaturesSource === SourceMyAlerts);
    const myAlertsMuniLayer = municipalFeatureLayers?.find(x => x.FeaturesSource === SourceMyAlerts);
    const hasMyAlertsLayer = (hasMapSelection && !!myAlertsMapLayer) || !!myAlertsMuniLayer;
    const isMyAlertsSelected = !!channels?.myAlertsApp?.send;
    if (hasMyAlertsLayer !== isMyAlertsSelected) {
      if (isMyAlertsSelected) errors.push('Alert.Channels.InvalidMyAlertsAppChannelRequiresSelection');
      else errors.push('Alert.Channels.InvalidMyAlertsAppChannelSelectionRequired');
    }

    return errors;
  }

  static validatePlaceholdersForPublicContent(alertModel: AlertModelService): string | null {
    if (alertModel.alertMessage.content?.publicContentPageRequested) {
      const message = HtmlHelper.convertHtmlToPlainText(alertModel.basicMessage?.messageBody);
      const textMessageChannel = alertModel.alertChannels?.textMessage;

      if (textMessageChannel?.send && textMessageChannel?.message && PlaceholderUtilities.inputHasContactPlaceholders(textMessageChannel.message)) {
        return 'Alert.Channels.InvalidPlaceholder.InvalidContent';
      }

      if (PlaceholderUtilities.inputHasContactPlaceholders(message)) {
        return 'Alert.Channels.InvalidPlaceholder.InvalidContent';
      }
    }
    
    return null;
  }

  static validatePlaceholdersForChannels(alertModel: AlertModelService, channelKeys?: string[]): string[] {
    if (!alertModel?.alertMessage?.customMessageVariables) return [];
    if (!alertModel?.alertChannels) return [];
    
    // get a list of `BasicChannel` models from our alert settings
    return this.iterableChannels(alertModel)
      // if specific channel keys were supplied, filter to those
      .filter(x => channelKeys?.includes(x.channelKey) ?? true)
      // map to a list of validation errors
      .map(x => this.validatePlaceholdersForSingleChannel(alertModel, x))
      // filter out null/undefined/empty values
      .filter(x => !!x?.length);
  }

  static validatePlaceholderBraces(alertModel: AlertModelService, channelKeys?: string[]): string[] {
    if (!alertModel?.alertMessage?.customMessageVariables) return [];
    if (!alertModel?.alertChannels) return [];
    
    // get a list of `BasicChannel` models from our alert settings
    return this.iterableChannels(alertModel)
      // if specific channel keys were supplied, filter to those
      .filter(x => channelKeys?.includes(x.channelKey) ?? true)
      // map to a list of validation errors
      .map(x => this.validateBracesForSingleChannel(alertModel, x))
      // filter out null/undefined/empty values
      .filter(x => !!x?.length);
  }

  private static readonly channelKeysSupportingContactPlaceholders = [this.KEY_EMAIL, this.KEY_TEXT_MESSAGE];
  static validatePlaceholdersForSingleChannel(alertModel: AlertModelService, channel: BasicChannel): string {
    if (!alertModel?.alertMessage?.customMessageVariables) return null;
    if (this.channelKeysSupportingContactPlaceholders.includes(channel?.channelKey)) return null;
    if (!channel?.send) return null;

    // Special voice channel case -- if the user has specified a custom voice file, we don't need to validate the voice channel
    if (channel.channelKey === this.KEY_VOICE && channel.message) return null;

    // Special ETN case -- if the user has specified a custom voice file, we don't need to validate the ETN channel
    if (channel.channelKey === this.KEY_ETN && alertModel.alertChannels.voice?.message) return null;

    let toValidate = channel.channelKey === this.KEY_VOICE ? channel.channelMessage : channel.message;

    // Special ETN case -- we need to validate the voice message rather than the message body (if it exists)
    if (!toValidate?.length && channel.channelKey === this.KEY_ETN) toValidate = alertModel.alertChannels.voice?.channelMessage;

    if (!toValidate?.length) toValidate = HtmlHelper.convertHtmlToPlainText(alertModel.basicMessage?.messageBody);
    if (!toValidate?.length) toValidate = alertModel.basicMessage?.message;
    if (!toValidate?.length) return null;

    const hasInvalidPlaceholders = PlaceholderUtilities.inputHasContactPlaceholders(toValidate);

    if (!hasInvalidPlaceholders) return null;

    const errorKey = `Alert.Channels.InvalidPlaceholder.${toPascalCase(channel.channelKey)}`;
    if (alertModel.translation?.has(errorKey, 'en')) return errorKey;

    return alertModel.translation?.translate('Alert.Channels.InvalidPlaceholder.ChannelParam', { channel: channel.channelName });
  }

  static validateBracesForSingleChannel(alertModel: AlertModelService, channel: BasicChannel): string {
    if (!alertModel?.alertMessage?.customMessageVariables) return null;
    if (!channel?.send) return null;

    let toValidate = channel.channelKey === this.KEY_VOICE ? channel.channelMessage : channel.message;
    if (!toValidate?.length) toValidate = HtmlHelper.convertHtmlToPlainText(alertModel.basicMessage?.messageBody);
    if (!toValidate?.length) toValidate = alertModel.basicMessage?.message;
    if (!toValidate?.length) return null;

    const hasInvalidBraces = !PlaceholderUtilities.bracesAreValid(toValidate);

    if (!hasInvalidBraces) return null;

    return alertModel.translation?.translate('Alert.Channels.InvalidPlaceholder.InvalidCurlyBraces', { channel: channel.channelName });
  }

  static validatePlaceholdersForSingleChannelKey(alertModel: AlertModelService, channelKey: string): string {
    const channel = this.iterableChannels(alertModel).find(x => x.channelKey === channelKey);
    return this.validatePlaceholdersForSingleChannel(alertModel, channel);
  }

  // for displaying to the alert sender which channels are currently customized (and will therefore ignore alert 
  // message update events)
  static buildCustomizedChannelsList(service: AlertModelService, alert: AlertDefaults = service.alertSettings): Array<string> {
    const channels = alert?.channels;
    if (!channels) return null;
    const list = new Array<string>();
    
    if (channels.textMessage?.send && channels.textMessage?.message) list.push(this.NAME_TEXT_MESSAGE);
    if (channels.voice?.send && channels.voice?.channelMessage) list.push(this.NAME_VOICE);
    if (channels.facebook?.send && channels.facebook?.message) list.push(this.NAME_FACEBOOK);
    if (channels.twitter?.send && channels.twitter?.message) list.push(this.NAME_TWITTER);
    if (channels.nextdoor?.send && channels.nextdoor?.message) list.push(this.NAME_NEXTDOOR);
    if (channels.microsoftTeams?.send && channels.microsoftTeams?.message) list.push(this.NAME_MICROSOFT_TEAMS);
    
    return list.length ? list : null;
  }

  static buildAudioFileNotFoundError(service: AlertModelService): RetryInfo {
    const voice = service.alertChannels?.voice;
    if (!voice?.send || !voice?.message) return;
    const errorMessage = 'Audio file \'' + voice.message + '\' not found!';
    return new RetryInfo(this.ERROR_AUDIO_FILE_NOT_FOUND, errorMessage, { filenane: voice.message, });
  }

  static buildCharacterCountText(translation: L10nTranslationService, messageLength: number, messageLimit: number): string {
    const key = messageLimit ? 'Alert.Channels.CharacterCount' : 'Alert.Channels.CharacterCountNoLimit';
    const params = {
      length: messageLength ? toString(messageLength) : '??',
      max: messageLimit ? toString(messageLimit) : '??',
    };
    
    return translation.translate(key, params);
  }

  static buildCharacterLimitValidationError(translation: L10nTranslationService, channelKey: string, messageLength: number, messageLimit: number = 0): string {
    if (!translation || !channelKey || !messageLength) return null;
    const limited = [this.KEY_TEXT_MESSAGE, this.KEY_TWITTER, this.KEY_NEXTDOOR, this.KEY_MICROSOFT_TEAMS];
    if (!limited.includes(channelKey)) return null;
    if (channelKey === this.KEY_TWITTER) messageLimit = messageLimit || SocialChannel.TWITTER_LIMIT;
    if (channelKey === this.KEY_NEXTDOOR) messageLimit = messageLimit || SocialChannel.NEXTDOOR_LIMIT;
    if (channelKey === this.KEY_MICROSOFT_TEAMS) messageLimit = messageLimit || AlertMicrosoftTeamsHelper.MICROSOFT_TEAMS_CHAR_LIMIT;
    if (!messageLimit || messageLength <= messageLimit) return null;

    const params = {
      channel: AlertChannelsHelper.getChannelName(channelKey),
    };

    return `${translation.translate('Alert.Channels.CharacterLimitValidationError', params)}: ${this.buildCharacterCountText(translation, messageLength, messageLimit)}`;
  }

  public static truncateMessage(message: string, maxCount: number): string {
    if(!maxCount || maxCount <= 0) return message;
    if (!message || message.length <= maxCount) return message;
    return this.slicePipe.transform(message, 0, maxCount);
  }
}
